import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth, useTheme } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { 
  MessageSquare, 
  Sun, 
  Moon, 
  ArrowLeft, 
  User,
  Palette,
  Bell,
  Shield,
  LogOut
} from 'lucide-react';

const SettingsPage = () => {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="glass sticky top-0 z-50 border-b border-border/50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <img src="/queryverse-logo2.png" alt="QueryVerse" className="h-8" />
            </Link>

            <div className="flex items-center gap-4">
              <button
                onClick={toggleTheme}
                className="p-2 rounded-full hover:bg-secondary transition-colors"
                data-testid="settings-theme-toggle"
              >
                {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              </button>
              <Button 
                variant="ghost"
                onClick={() => navigate('/chat')} 
                className="rounded-full"
                data-testid="settings-back-btn"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Chat
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-6 py-12">
        <div className="mb-8">
          <h1 className="text-3xl font-semibold tracking-tight font-['Manrope']">
            Settings
          </h1>
          <p className="mt-2 text-muted-foreground">
            Manage your account and preferences
          </p>
        </div>

        <div className="space-y-6">
          {/* Profile Section */}
          <Card className="rounded-2xl border-border/50">
            <CardHeader>
              <CardTitle className="font-['Manrope'] flex items-center gap-2">
                <User className="h-5 w-5" />
                Profile
              </CardTitle>
              <CardDescription>
                Your account information
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Name</Label>
                <Input 
                  id="name" 
                  value={user?.name || ''} 
                  disabled 
                  className="rounded-xl"
                  data-testid="settings-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input 
                  id="email" 
                  value={user?.email || ''} 
                  disabled 
                  className="rounded-xl"
                  data-testid="settings-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Member since</Label>
                <p className="text-sm text-muted-foreground">
                  {user?.created_at ? new Date(user.created_at).toLocaleDateString('en-IN', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric'
                  }) : 'N/A'}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Appearance Section */}
          <Card className="rounded-2xl border-border/50">
            <CardHeader>
              <CardTitle className="font-['Manrope'] flex items-center gap-2">
                <Palette className="h-5 w-5" />
                Appearance
              </CardTitle>
              <CardDescription>
                Customize how QueryVerse looks
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <Label>Dark Mode</Label>
                  <p className="text-sm text-muted-foreground">
                    Switch between light and dark themes
                  </p>
                </div>
                <Switch 
                  checked={theme === 'dark'}
                  onCheckedChange={toggleTheme}
                  data-testid="dark-mode-switch"
                />
              </div>
            </CardContent>
          </Card>

          {/* Subscription Info */}
          <Card className="rounded-2xl border-border/50">
            <CardHeader>
              <CardTitle className="font-['Manrope'] flex items-center gap-2">
                <Shield className="h-5 w-5" />
                Subscription
              </CardTitle>
              <CardDescription>
                Your current plan details
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <p className="font-medium capitalize">{user?.subscription_plan || 'Free'} Plan</p>
                  <p className="text-sm text-muted-foreground">
                    {user?.subscription_plan === 'free' 
                      ? `${user?.messages_limit || 20} messages per day`
                      : 'Unlimited messages'
                    }
                  </p>
                </div>
                <Button 
                  variant="outline" 
                  onClick={() => navigate('/billing')} 
                  className="rounded-full"
                  data-testid="manage-billing-btn"
                >
                  Manage Billing
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Danger Zone */}
          <Card className="rounded-2xl border-destructive/50">
            <CardHeader>
              <CardTitle className="font-['Manrope'] flex items-center gap-2 text-destructive">
                <LogOut className="h-5 w-5" />
                Sign Out
              </CardTitle>
              <CardDescription>
                Sign out of your account on this device
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button 
                variant="destructive" 
                onClick={() => { logout(); navigate('/'); }}
                className="rounded-full"
                data-testid="logout-btn"
              >
                Sign Out
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
